
import React, { useState } from 'react';
import { Job } from "@/components/jobs/types";
import { Card, CardContent, CardHeader } from "@/components/ui/card";

import { MatchingCandidatesHeader } from "./matching-candidates/MatchingCandidatesHeader";
import { MatchingCandidatesFilters } from "./matching-candidates/MatchingCandidatesFilters";
import { MatchingCandidatesList } from "./matching-candidates/MatchingCandidatesList";
import { MatchingCandidatesPagination } from "./matching-candidates/MatchingCandidatesPagination";
import { NoMatchingCandidates } from "./matching-candidates/NoMatchingCandidates";
import { NotLoggedInMessage } from "./matching-candidates/NotLoggedInMessage";
import { MatchingCandidatesErrorState } from "./matching-candidates/MatchingCandidatesErrorState";
import { MatchingCandidatesLoadingState } from "./matching-candidates/MatchingCandidatesLoadingState";
import { DemoModeToggle } from "./matching-candidates/DemoModeToggle";
import { useMatchingCandidatesLogic } from "./matching-candidates/hooks/useMatchingCandidatesLogic";
import type { SuggestedCandidate } from "@/types/candidateSuggestion";

interface MatchingCandidatesProps {
  job: Job;
}

// Mock candidates for demo mode
const mockCandidates: SuggestedCandidate[] = [
  {
    id: 'mock-1',
    first_name: 'Sarah',
    last_name: 'Johnson',
    email: 'sarah.johnson@email.com',
    phone: '+1 (555) 123-4567',
    location_city: 'San Francisco',
    location_state: 'CA',
    remote_ok: true,
    experience_years: 5,
    skills: ['React', 'TypeScript', 'Node.js', 'GraphQL'],
    status: 'new',
    match_score: 95,
    match_reasons: [
      'Perfect skill match with React and TypeScript',
      'Strong experience with Node.js and GraphQL',
      'Located in preferred market area',
      'Open to remote work'
    ],
    notes: 'Highly qualified candidate with excellent portfolio',
    last_contact: '2024-01-15T10:00:00Z',
    resume_url: null,
    linkedin_url: 'https://linkedin.com/in/sarah-johnson'
  },
  {
    id: 'mock-2',
    first_name: 'Michael',
    last_name: 'Chen',
    email: 'michael.chen@email.com',
    phone: '+1 (555) 987-6543',
    location_city: 'Austin',
    location_state: 'TX',
    remote_ok: false,
    experience_years: 8,
    skills: ['JavaScript', 'React', 'Python', 'AWS'],
    status: 'screening',
    match_score: 88,
    match_reasons: [
      'Strong technical background in required technologies',
      'Extensive experience with cloud platforms',
      'Previous experience in similar role'
    ],
    notes: 'Senior level candidate, currently in screening process',
    last_contact: '2024-01-10T14:30:00Z',
    resume_url: null,
    linkedin_url: 'https://linkedin.com/in/michael-chen'
  },
  {
    id: 'mock-3',
    first_name: 'Emily',
    last_name: 'Rodriguez',
    email: 'emily.rodriguez@email.com',
    phone: '+1 (555) 456-7890',
    location_city: 'New York',
    location_state: 'NY',
    remote_ok: true,
    experience_years: 3,
    skills: ['Vue.js', 'JavaScript', 'CSS', 'MongoDB'],
    status: 'interviewing',
    match_score: 82,
    match_reasons: [
      'Strong frontend development skills',
      'Experience with modern JavaScript frameworks',
      'Good cultural fit based on initial screening'
    ],
    notes: 'Currently in interview process, showing strong potential',
    last_contact: '2024-01-12T09:15:00Z',
    resume_url: null,
    linkedin_url: 'https://linkedin.com/in/emily-rodriguez'
  }
];

export const MatchingCandidates = ({ job }: MatchingCandidatesProps) => {
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('match_score');
  const [isDemoMode, setIsDemoMode] = useState(true);
  const [filters, setFilters] = useState({
    experienceLevel: [] as string[],
    location: [] as string[],
    skills: [] as string[],
    availability: 'any',
    salaryRange: [0, 200000] as [number, number]
  });

  const {
    user,
    isLoading,
    error,
    minMatchScore,
    handleMinMatchScoreChange,
    paginatedCandidates,
    currentPage,
    totalPages,
    handlePageChange,
    noResultsAfterFilter,
    suggestedCandidates 
  } = useMatchingCandidatesLogic(job);

  const handleRefresh = () => {
    console.log('Refreshing candidates...');
  };

  const handleClearFilters = () => {
    setFilters({
      experienceLevel: [],
      location: [],
      skills: [],
      availability: 'any',
      salaryRange: [0, 200000]
    });
  };

  // Use mock data in demo mode, real data otherwise
  const displayCandidates = isDemoMode ? mockCandidates : paginatedCandidates;
  const totalCandidates = isDemoMode ? mockCandidates.length : (suggestedCandidates?.length || 0);
  const filteredCandidates = displayCandidates.length;

  if (!user) {
    return <NotLoggedInMessage />;
  }
  
  if (isLoading && !isDemoMode) {
    return <MatchingCandidatesLoadingState />;
  }

  if (error && !isDemoMode) {
    return <MatchingCandidatesErrorState error={error} />;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="space-y-4">
          <MatchingCandidatesHeader
            minMatchScore={minMatchScore}
            onMinMatchScoreChange={handleMinMatchScoreChange}
            totalCandidates={totalCandidates}
            filteredCandidates={filteredCandidates}
            sortBy={sortBy}
            onSortChange={setSortBy}
            onRefresh={handleRefresh}
            showFilters={showFilters}
            onToggleFilters={() => setShowFilters(!showFilters)}
          />
          
          <DemoModeToggle
            isDemoMode={isDemoMode}
            onToggle={setIsDemoMode}
          />
        </CardHeader>
        <CardContent className="space-y-4">
          {showFilters && (
            <MatchingCandidatesFilters
              minMatchScore={minMatchScore}
              onMinMatchScoreChange={handleMinMatchScoreChange}
              filters={filters}
              onFiltersChange={setFilters}
              onClearFilters={handleClearFilters}
            />
          )}
          
          {displayCandidates.length > 0 ? (
            <>
              <MatchingCandidatesList candidates={displayCandidates} job={job} />
              {!isDemoMode && (
                <MatchingCandidatesPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              )}
            </>
          ) : (
            <NoMatchingCandidates 
              noResultsAfterFilter={noResultsAfterFilter}
              noInitialSuggestions={!isDemoMode && (!suggestedCandidates || suggestedCandidates.length === 0)}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};
